import { phasesFor, type Phase, type PipelineProfile, type Role, type RunStatus } from "@procurement-agents/scope-api";
import { PHASES, ROLE_LABEL, STEP_VERBS } from "../labels";

/** The stepper shows the phases the run's pipeline profile keeps (SPEC_SLICE6): a run without market research or QA never lists them.
 *  A run that finished without an SME review (skipped, or nothing needed one) does not list that step either. */
export function Journey({ phase, status, pendingRole, canResume, profile, smeSkipped, selectable, selected, onSelect }: { phase: Phase; status: RunStatus; pendingRole?: Role | null; canResume: boolean; profile?: PipelineProfile | null; smeSkipped?: boolean; selectable?: Phase[]; selected?: Phase | null; onSelect?: (phase: Phase) => void }) {
  const kept = new Set(phasesFor(profile, PHASES.map((p) => p.id)));
  if (smeSkipped) kept.delete("sme");
  const steps = PHASES.filter((p) => kept.has(p.id));
  // a finished run has every step done, Finish included: ticks throughout, no ring left on the last step
  const idx = status === "complete" ? steps.length : steps.findIndex((p) => p.id === phase);
  return (
    <nav className="journey" aria-label="Progress">
      {steps.map((p, i) => {
        const cls = ["step", i < idx ? "done" : "", i === idx ? "current" : "upcoming", selected === p.id ? "selected" : ""].join(" ");
        const inner = <>
          {/* a tick for done, a ring for where the run is, a plain dot for what is to come */}
          <span className="dot" aria-hidden>{i < idx ? <TickIcon /> : null}</span>
          <span className="step-label">{STEP_VERBS[p.id] ?? p.label}</span>
          {i < idx && <span className="sr-only">, completed</span>}{i === idx && <span className="sr-only">, current step</span>}
          {i === idx && status === "waiting" && (canResume ? <span className="chip you">you</span> : pendingRole ? <span className="chip">waiting on {ROLE_LABEL[pendingRole]}</span> : null)}
        </>;
        // a done step the run keeps readable (the intake conversation) is a button; everything else stays plain
        return onSelect && i < idx && selectable?.includes(p.id)
          ? <button key={p.id} type="button" className={cls} onClick={() => onSelect(p.id)} aria-pressed={selected === p.id}>{inner}</button>
          : <div key={p.id} className={cls}>{inner}</div>;
      })}
    </nav>
  );
}

function TickIcon() {
  return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5 10 17.5 19 7" /></svg>;
}
